import { useAuthorization } from "@/hooks/use-authorization";
import { FORM_ASSIGNMENTS_STATUS } from "@/types/entities/formality-request";
import {
  getInternalDeadlineFromFormData,
  getRelevantAssignment,
  getRemainingTime,
  isTimerDisabled,
} from "@/utils/procedures-ui";
import {
  Badge,
  Button,
  Icon,
  RowCard,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";
import dayjs from "dayjs";
import { NavLink, useParams } from "react-router";
import { Permissions } from "@/utils/permissions";
import { getBadge } from "../form-badges";
import { cn } from "@/utils/theme";
import { useMemo } from "react";
import { UsersListTooltip } from "@/components/common/userlist-tooltip";
import {
  mapInteractionsToUsersList,
  type NodeInteraction,
} from "@/components/common/userlist-tooltip.utils";
import { useInfo } from "@/pages/procedures/hooks/use-info";

export interface FormAssignment {
  form_id?: string | null;
  status: keyof typeof FORM_ASSIGNMENTS_STATUS;
  filled_form_id?: string | null;
  assigned_at?: string | null;
  form?: {
    target_user_type: string;
    id: string;
  } | null;
}

export interface FormNode {
  id: string;
  started_at: string | Date;
  node: {
    display_name: string;
  };
  form_assignments?: FormAssignment[];
  user_form_assignments?: FormAssignment[];
  interactions?: NodeInteraction[];
}

export const FormCard = ({
  node,
  procedureId,
  index,
  formAssignment,
}: {
  node: FormNode;
  procedureId: string;
  index: number;
  formAssignment: string;
}) => {
  const params = useParams<{
    procedureId: string;
    id?: string;
    registryId?: string;
  }>();
  const { isCancelled } = useInfo();
  const { hasPermission } = useAuthorization();
  const canUpdate = hasPermission(Permissions.formality_requests.edit);

  const assigment = node.form_assignments?.[0];
  // Un formulario interno asignado a un grupo genera una fila por miembro, así
  // que hay que elegir la que refleja el estado del nodo (ver
  // `getRelevantAssignment`) en lugar de la última del arreglo.
  const userAssignment = getRelevantAssignment(node.user_form_assignments);
  const isPending =
    !node.form_assignments || node.form_assignments.length === 0;
  const needsCorrection =
    assigment?.status === FORM_ASSIGNMENTS_STATUS.CORRECTION;
  const isInCorrectionFilling =
    assigment?.status === FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION;
  const userHasFilledForm = node.form_assignments?.[0]?.filled_form_id;
  const internalDeadline = getInternalDeadlineFromFormData(
    node as
      | Partial<{
          form_assignments?: Array<{
            revisions?: Array<{
              internal_deadline: string | null;
            }>;
          }>;
        }>
      | undefined,
  );
  const isExpiredCard =
    internalDeadline && Date.now() > internalDeadline.getTime();
  const timerDisabled = isTimerDisabled(
    node as
      | Partial<{
          form_assignments?: Array<{
            revisions?: Array<{
              internal_started_at?: string | null;
              internal_deadline?: string | null;
            }>;
          }>;
        }>
      | undefined,
  );
  const hasStarted = timerDisabled;

  const startedAt = dayjs(node.started_at).format("DD/MM/YYYY HH:mm A");
  const status = userAssignment?.status || node.form_assignments?.[0]?.status;

  // SUBMITTED is the terminal state of an internal form assignment: the backend
  // never promotes it further, so an analyst form left in SUBMITTED is finished
  // work and reads as COMPLETED. Applicant forms keep their own status, where
  // SUBMITTED still means "sent, pending review".
  const displayStatus =
    userAssignment?.status === FORM_ASSIGNMENTS_STATUS.SUBMITTED
      ? FORM_ASSIGNMENTS_STATUS.COMPLETED
      : status;
  const badge = getBadge(
    displayStatus as keyof typeof FORM_ASSIGNMENTS_STATUS,
    isCancelled,
  );

  const isAnalyst = userAssignment?.form?.target_user_type === "analyst";

  const isDisabled = (isPending || !userHasFilledForm) && !isAnalyst;

  const redirectUrl = useMemo(() => {
    if (params.id && params.registryId) {
      return `/expedientes/entidad/${params.id}/detalle-registro/${params.registryId}/tramite`;
    }
    return `/tramite`;
  }, [params]);

  const isPendingStatus =
    status === "PENDING" || status === "ASSIGNED" || status === "IN_PROGRESS";

  const formUrl = useMemo(() => {
    if (needsCorrection && canUpdate) {
      return `${redirectUrl}/${procedureId}/correccion/${node?.form_assignments?.[0]?.form_id}?assigment=${formAssignment}`;
    }

    if (isAnalyst) {
      if (isPendingStatus) {
        return `${redirectUrl}/${procedureId}/formulario-interno/${userAssignment?.form_id}?assigment=${formAssignment}`;
      }
      return `${redirectUrl}/${procedureId}/${userAssignment?.form_id}?assigment=${formAssignment}`;
    }

    return `${redirectUrl}/${procedureId}/${node?.form_assignments?.[0]?.form_id}?assigment=${formAssignment}`;
  }, [
    needsCorrection,
    canUpdate,
    redirectUrl,
    procedureId,
    node,
    formAssignment,
    isAnalyst,
    isPendingStatus,
    userAssignment,
  ]);

  return (
    <NavLink
      onClick={(e) => {
        if (isDisabled || isCancelled) e.preventDefault();
      }}
      className={cn(
        isDisabled &&
          "**:text-on-disabled! **:cursor-not-allowed cursor-not-allowed",
      )}
      to={formUrl}
    >
      <RowCard
        key={node.id ?? index}
        title={
          <div className="flex items-center gap-1">
            {/* Timer activo - normal countdown */}
            {internalDeadline &&
              !isExpiredCard &&
              needsCorrection &&
              !timerDisabled && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      size="sm"
                      shape="square"
                      variant="warning"
                      fill="ghost"
                      className="w-6 h-6 flex items-center justify-center cursor-pointer"
                    >
                      <Icon
                        type="fas"
                        name="clock"
                        size="sm"
                        className="text-warning-quiet"
                      />
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start">
                    <p className="font-geist-mono text-[#f59e0b] font-semibold">
                      Tiempo restante:{" "}
                      <span className="text-body-quiet font-normal">
                        {getRemainingTime(internalDeadline)}
                      </span>
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}

            {/* Timer deshabilitado - proceso ya iniciado */}
            {internalDeadline &&
              !isExpiredCard &&
              needsCorrection &&
              timerDisabled &&
              hasStarted && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-default shrink-0 gap-1 px-1.5 [&_svg]:shrink-0 border-[0.5px] font-normal transition-all bg-surface-warning-accent-quiet border-contextual-warning text-warning h-6 text-sm leading-2xl rounded-lg hover:bg-surface-warning-accent flex items-center w-fit">
                      <Icon
                        size="xs"
                        type="fas"
                        name="clock"
                        className="text-warning-quiet"
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start">
                    <span className="text-yellow-500">Tiempo restante:</span>{" "}
                    {getRemainingTime(internalDeadline, false)}
                  </TooltipContent>
                </Tooltip>
              )}

            {/* Timer deshabilitado - proceso NO iniciado */}
            {internalDeadline &&
              !isExpiredCard &&
              needsCorrection &&
              timerDisabled &&
              !hasStarted && (
                <span className="cursor-default shrink-0 gap-1 px-2 [&_svg]:shrink-0 py-0.5 border-[0.5px] font-normal transition-all bg-neutral-object-accent-silent border-neutral text-body h-6 text-sm leading-2xl rounded-md hover:bg-neutral-object-accent-quiet hover:text-body-loud flex items-center w-fit opacity-60">
                  {getRemainingTime(internalDeadline, false)}
                  <i
                    className="fas fa-pause fa-xs text-neutral-quiet"
                    aria-hidden="true"
                  ></i>
                </span>
              )}

            <p className="font-normal text-base text-body-loud truncate leading-tight">
              {node.node.display_name} {isAnalyst && "(Analista)"}
            </p>
          </div>
        }
        color={(() => {
          if (
            isCancelled &&
            displayStatus === FORM_ASSIGNMENTS_STATUS.IN_PROGRESS
          ) {
            return "neutral";
          }
          if (isAnalyst && userAssignment?.status === "SUBMITTED") {
            return "success";
          }
          if (needsCorrection) return "info";
          if (isInCorrectionFilling) return "warning";
          if (
            isPending ||
            !userHasFilledForm ||
            userAssignment?.status === "ASSIGNED"
          )
            return "neutral";
          if (assigment?.status === FORM_ASSIGNMENTS_STATUS.COMPLETED) {
            return "success";
          }
          if (assigment?.status === FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION) {
            return "danger";
          }
          return "action";
        })()}
        icon={{
          name: "bars",
          type: "fas",
          size: "lg",
        }}
        subtitle={`Iniciado ${startedAt}`}
      >
        <RowCard.Right>
          {badge}

          {node.interactions && node.interactions.length > 0 && (
            <UsersListTooltip
              items={mapInteractionsToUsersList(node.interactions)}
            />
          )}

          <Button
            onClick={() => console.log(node)}
            variant="ghost"
            size="xs"
            className="size-8"
            disabled={isCancelled}
          >
            <Icon type="far" name="arrow-up-right" />
          </Button>
        </RowCard.Right>
      </RowCard>
    </NavLink>
  );
};
